import { JiraIcon, TrelloIcon } from '@atlaskit/logo';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';

import AirtableIcon from '../../assets/Airtable.svg';
import AsanaIcon from '../../assets/Asana.svg';
import ClickUpIcon from '../../assets/ClickUp.svg';
import GitHubIcon from '../../assets/git-hub';
import GoogleSheetIcon from '../../assets/GoogleSheet.svg';
import LinearIcon from '../../assets/linear';
import MondayIcon from '../../assets/Monday.svg';
import MSExcelIcon from '../../assets/MSExcel.svg';
import SmartSheetIcon from '../../assets/smart-sheet';

import { IconType, type Icon } from '../../ui/icon';
import {
	type ToolNameKey,
	type SupportedThirdParty,
	JIRA_SOFTWARE,
	TRELLO,
	SHEETS,
	EXCEL,
	MONDAY,
	ASANA,
	CLICK_UP,
	AIRTABLE,
	SMART_SHEET,
	GITHUB,
	LINEAR,
	MONDAY_DISPLAY_NAME,
	TRELLO_DISPLAY_NAME,
	GOOGLE_SHEETS_DISPLAY_NAME,
	EXCEL_DISPLAY_NAME,
	ASANA_DISPLAY_NAME,
	CLICK_UP_DISPLAY_NAME,
	AIRTABLE_DISPLAY_NAME,
	SMART_SHEET_DISPLAY_NAME,
	GITHUB_DISPLAY_NAME,
	LINEAR_DISPLAY_NAME,
	JIRA_DISPLAY_NAME,
	JIRA_BUSINESS,
} from '../import-type';
import { ProjectTypes } from '../project';
import messages from './messages';

export type SupportedTool = {
	key: SupportedThirdParty;
	name: string;
	icon: Icon;
	isSupported: true;
	requiresOAuth: boolean;
	showPreOAuthModal: boolean;
	requiresPersonalAccessToken: boolean;
	label?: string;
};

export type Tool =
	| {
			key: ToolNameKey;
			name: string;
			icon: Icon;
			isSupported: false;
			label?: string;
	  }
	| SupportedTool;

export const toolMap: Record<ToolNameKey, Tool> = {
	[JIRA_SOFTWARE]: {
		key: JIRA_SOFTWARE,
		name: JIRA_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: JiraIcon,
			props: {
				size: 'large',
				appearance: 'brand',
			},
		},
		isSupported: false,
		label: messages.jiraAppLabelSpork.defaultMessage,
	},
	[TRELLO]: {
		key: TRELLO,
		name: TRELLO_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: TrelloIcon,
			props: { size: 'large', appearance: 'brand' },
		},
		isSupported: false,
	},
	[SHEETS]: {
		key: SHEETS,
		name: GOOGLE_SHEETS_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: GoogleSheetIcon, alt: GOOGLE_SHEETS_DISPLAY_NAME },
		},
		isSupported: false,
	},
	[EXCEL]: {
		key: EXCEL,
		name: EXCEL_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: MSExcelIcon, alt: EXCEL_DISPLAY_NAME },
		},
		isSupported: false,
	},
	[MONDAY]: {
		key: MONDAY,
		name: MONDAY_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: MondayIcon, alt: 'Monday Icon' },
		},
		isSupported: false,
	},
	[ASANA]: {
		key: ASANA,
		name: ASANA_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: AsanaIcon, alt: 'Asana Icon' },
		},
		isSupported: false,
	},
	[CLICK_UP]: {
		key: CLICK_UP,
		name: CLICK_UP_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: ClickUpIcon, alt: CLICK_UP_DISPLAY_NAME },
		},
		isSupported: false,
	},
	[AIRTABLE]: {
		key: AIRTABLE,
		name: AIRTABLE_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: AirtableIcon, alt: AIRTABLE_DISPLAY_NAME },
		},
		isSupported: false,
	},
	[SMART_SHEET]: {
		key: SMART_SHEET,
		name: SMART_SHEET_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: SmartSheetIcon,
			props: {},
		},
		isSupported: false,
	},
	[GITHUB]: {
		key: GITHUB,
		name: GITHUB_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: GitHubIcon,
			props: {},
		},
		isSupported: false,
	},
	[LINEAR]: {
		key: LINEAR,
		name: LINEAR_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: LinearIcon,
			props: {},
		},
		isSupported: false,
	},
	[JIRA_BUSINESS]: {
		key: JIRA_BUSINESS,
		name: JIRA_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: JiraIcon,
			props: {
				size: 'large',
				appearance: 'brand',
			},
		},
		isSupported: false,
		label: messages.jiraBusinessAppLabelNonFinal.defaultMessage,
	},
};

const getJWMTools = (): Tool[] => [
	{
		...toolMap[JIRA_SOFTWARE],
		key: JIRA_SOFTWARE,
		isSupported: true,
		requiresOAuth: false,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: false,
	},
	{
		...toolMap[TRELLO],
		key: TRELLO, // have to add key here to make this object SupportedThirdParty type.
		isSupported: true,
		requiresOAuth: false,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: false,
	},
	{
		...toolMap[MONDAY],
		key: MONDAY,
		isSupported: true,
		requiresOAuth: false,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: true,
	},
	{
		...toolMap[ASANA],
		key: ASANA,
		showPreOAuthModal: false,
		isSupported: true,
		requiresOAuth: true,
		requiresPersonalAccessToken: false,
	},
	{
		...toolMap[CLICK_UP],
		key: CLICK_UP,
		isSupported: fg('jwm_clickup_importer'),
		requiresOAuth: true,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: false,
	},
	toolMap[SHEETS],
	toolMap[EXCEL],
	toolMap[AIRTABLE],
	toolMap[SMART_SHEET],
];

const getJWMNonAdminTools = (): Tool[] => [
	{
		...toolMap[TRELLO],
		key: TRELLO,
		isSupported: true,
		requiresOAuth: false,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: false,
	},
	{
		...toolMap[ASANA],
		key: ASANA,
		showPreOAuthModal: false,
		isSupported: true,
		requiresOAuth: true,
		requiresPersonalAccessToken: false,
	},
	toolMap[MONDAY],
	toolMap[JIRA_SOFTWARE],
	toolMap[SHEETS],
	toolMap[EXCEL],
	toolMap[CLICK_UP],
	toolMap[AIRTABLE],
	toolMap[SMART_SHEET],
];

const getJSWTools = (): Tool[] => [
	{
		...toolMap[TRELLO],
		key: TRELLO,
		isSupported: ff('enable-trello-importer-in-jsw-x2c_k25qu'),
		requiresOAuth: false,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: false,
	},
	{
		...toolMap[ASANA],
		key: ASANA,
		showPreOAuthModal: false,
		isSupported: ff('enable-x2c-asana-jsw-importer_ygp38'),
		requiresOAuth: true,
		requiresPersonalAccessToken: false,
	},
	toolMap[MONDAY],
	toolMap[CLICK_UP],
	toolMap[GITHUB],
	toolMap[LINEAR],
];

const getJSWToolsWithBusinessProject = (): Tool[] => [
	{
		...toolMap[JIRA_BUSINESS],
		key: JIRA_BUSINESS,
		isSupported: true,
		requiresOAuth: false,
		showPreOAuthModal: false,
		requiresPersonalAccessToken: false,
	},
	...getJSWTools(),
];

export const getTools = (type: ProjectType | null, isSiteAdmin = true) => {
	switch (type) {
		case ProjectTypes.BUSINESS:
			return ff('gun-2207-non-admin-support-in-tmp-migration_k7z9p') && !isSiteAdmin
				? getJWMNonAdminTools()
				: getJWMTools();
		case ProjectTypes.SOFTWARE:
			return ff('jira-business-to-software-project-imports')
				? getJSWToolsWithBusinessProject()
				: getJSWTools();
		default:
			return [];
	}
};
